import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';
import { ControllerRenderProps } from 'react-hook-form';

import { WholesaleItem, WholesalesListResponse } from '@domains/login';

import { wholesalesListFx } from '@stores/login';

import { FORMAT, REG_EXP_RULES } from '@utils/constants';

import { useClickOutside } from '@hooks/click-outside';
import { useCombinedRef } from '@hooks/use-combined-ref';
import { useDebounce } from '@hooks/use-debounce';

import { Spinner } from '@components/common/Spinner/Spinner';
import { MaskedPatternInput, StyledTextArea } from '@components/common/StyledInput';
import type { FormValues } from '@components/login-dialog/LoginDialogLegalLoginForm/LoginDialogLegalLoginForm';

import styles from './InnInput.module.scss';

type InnInputProps = {
    onWholesaleSelect: (wholesale: WholesaleItem | null) => void;
    selectedWholesale: WholesaleItem | null;
    className?: string;
    disabled?: boolean;
    hasError?: boolean;
    errorMessage?: string;
} & Omit<ControllerRenderProps<FormValues, 'inn'>, 'ref'>;

export const InnInput = forwardRef<HTMLInputElement, InnInputProps>((props, ref) => {
    const {
        onWholesaleSelect,
        disabled = false,
        hasError = false,
        errorMessage,
        selectedWholesale,
        className,
        ...inputProps
    } = props;

    const { value } = inputProps;

    const [debouncedInn] = useDebounce(value, 400);
    const [showMaskedInput, setShowMaskedInput] = useState(true);

    const [isShowResults, setShowResults] = useState(false);
    const [wholesales, setWholesales] = useState<WholesalesListResponse['items']>([]);
    const [showError, setShowError] = useState<boolean>(false);
    const [isSelectingWholesale, setIsSelectingWholesale] = useState(false);

    const isInnValid = REG_EXP_RULES.inn.test(debouncedInn);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const combinedRef = useCombinedRef(ref, inputRef);

    useEffect(() => {
        if (typeof combinedRef === 'function') {
            combinedRef(inputRef.current);
        } else if (combinedRef) {
            combinedRef.current = inputRef.current;
        }
    }, [combinedRef]);

    const { getWholesalesList, isLoading } = useUnit({
        getWholesalesList: wholesalesListFx,
        isLoading: wholesalesListFx.pending,
    });

    useClickOutside({
        ref: wrapperRef,
        action: () => {
            if (!isSelectingWholesale) {
                setShowResults(false);
            }
        },
    });

    useEffect(
        () => {
            if (isInnValid) {
                setShowResults(true);
                getWholesalesList({ inn: debouncedInn })
                    .then((response) => {
                        setWholesales(response.items);
                        setShowError(response.items.length === 0);
                    })
                    .catch(() => {
                        setWholesales([]);
                        setShowError(true);
                    });
            } else {
                onWholesaleSelect(null);
                setWholesales([]);
                setShowResults(false);
            }
        },
        [isInnValid, debouncedInn], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleWholesaleClick = (wholesale: WholesaleItem) => {
        setIsSelectingWholesale(true);
        onWholesaleSelect(wholesale);
        setShowMaskedInput(false);

        setTimeout(() => {
            setIsSelectingWholesale(false);
            setShowResults(false);
        }, 100);
    };

    const handleFocus = () => {
        setShowMaskedInput(true);
        if (isInnValid) {
            setShowResults(true);
        }
    };

    const handleBlur = () => {
        if (selectedWholesale && !isSelectingWholesale) {
            setShowMaskedInput(false);
        }
    };

    const handleTextAreaClick = () => {
        setShowMaskedInput(true);

        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);

        if (isInnValid) {
            setShowResults(true);
        }
    };

    const maskedInputProps = {
        ...inputProps,
        ref: combinedRef,
        mask: '',
        patternChar: '#',
        valueIsNumericString: true,
        isKeepHintPlace: true,
        hintMessage: errorMessage,
        hasError: hasError || showError,
        disabled,
        onFocus: handleFocus,
        className: styles.input,
        autoComplete: 'off',
    } as const;

    return (
        <div className={clsx(styles.container, className)} ref={wrapperRef}>
            {showMaskedInput && (
                <>
                    <MaskedPatternInput
                        {...maskedInputProps}
                        label="ИНН"
                        format={FORMAT.inn12}
                        onBlur={handleBlur}
                    />
                    <div className={clsx(styles.results, !isShowResults && styles.isHidden)}>
                        <div className={styles.organizationsList}>
                            {isLoading ? (
                                <div className={styles.spinnerWrapper}>
                                    <Spinner size="medium" />
                                </div>
                            ) : (
                                <>
                                    {wholesales.map((wholesale) => (
                                        <button
                                            key={wholesale.id}
                                            type="button"
                                            className={clsx(
                                                styles.organizationItem,
                                                selectedWholesale?.id === wholesale.id &&
                                                    styles.isSelected,
                                            )}
                                            onClick={() => handleWholesaleClick(wholesale)}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                            }}
                                        >
                                            {wholesale.name}
                                        </button>
                                    ))}
                                    {wholesales.length === 0 && (
                                        <p className={styles.emptyState}>Ничего не найдено</p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            {!showMaskedInput && selectedWholesale && (
                <StyledTextArea
                    value={selectedWholesale.name}
                    readOnly
                    className={styles.textarea}
                    disabled={disabled}
                    isKeepHintPlace
                    minRows={1}
                    maxRows={5}
                    onClick={handleTextAreaClick}
                />
            )}
        </div>
    );
});
